import { isInstantFresh } from '@/lib/datetime';
import type { CampaignStatus, CampaignOperationalState, CampaignOperationalStatus } from '@/api/types';

export type CampaignStatusKind = CampaignOperationalState;

/** The campaign shape this needs — anything carrying the two fields will do. */
export interface ArmableCampaign {
  status?: CampaignStatus | null;
  operational_status?: CampaignOperationalStatus | null;
  auto_launch_requested_at?: string | null;
}

/**
 * Armed *only* while the campaign is still a draft.
 *
 * The sweep clears the flag when it launches, but clearing is a separate write from the
 * status change, so a reader that trusted the timestamp alone would show a live campaign
 * as armed for as long as the two were out of step.
 */
export function campaignStatusKind(
  status: CampaignStatus | null | undefined,
  autoLaunchRequestedAt: string | null | undefined,
  operational?: CampaignOperationalStatus | null,
): CampaignStatusKind {
  if (operational) {
    if (['live', 'paused', 'paused_by_system', 'starting', 'partially_active', 'scheduled'].includes(operational.state)
      && (operational.stale || !isInstantFresh(operational.observed_at, 30 * 60_000))) {
      return operational.reason ? 'needs_attention' : 'unverified';
    }
    return operational.state;
  }
  const base = (status ?? 'draft') as CampaignStatus;
  if (base === 'live' || base === 'paused') return 'unverified';
  return base === 'draft' && !!autoLaunchRequestedAt ? 'armed' : base;
}

export function campaignKindOf(campaign: ArmableCampaign | null | undefined): CampaignStatusKind {
  return campaignStatusKind(campaign?.status, campaign?.auto_launch_requested_at, campaign?.operational_status);
}

export function isArmed(campaign: ArmableCampaign | null | undefined): boolean {
  return campaignKindOf(campaign) === 'armed';
}

const LABELS: Record<CampaignStatusKind, string> = {
  draft: 'draft',
  armed: 'armed',
  ready_to_publish: 'ready to publish',
  live: 'live',
  paused: 'paused',
  paused_by_system: 'paused by system',
  pausing: 'pausing',
  scheduled: 'scheduled',
  starting: 'starting / in review',
  partially_active: 'partially active',
  needs_attention: 'needs attention',
  unverified: 'status unverified',
  ended: 'ended',
};

/** Lowercase; the status chip uppercases in CSS, and exports want the plain word. */
export function campaignStatusLabel(kind: CampaignStatusKind): string {
  return LABELS[kind] ?? kind;
}

/**
 * The list's word for a row. An archived campaign projects `pausing` until its stop is
 * seen after the archive and `needs_attention` while it is still seen delivering
 * (2026-10-21); "pausing" would read as an operator's pause, so it says what Archive is
 * doing instead, in the status line's words.
 */
export function campaignListStatusLabel(
  status: CampaignStatus | null | undefined,
  kind: CampaignStatusKind,
): string {
  if (status === 'ended' && kind === 'pausing') return 'stopping delivery';
  if (status === 'ended' && kind === 'needs_attention') return 'stop not confirmed';
  return campaignStatusLabel(kind);
}

interface PreflightCheckLike {
  status?: string | null;
  message?: string | null;
}

/**
 * Why an armed campaign has not launched yet, in the server's own words.
 *
 * The sweep runs every minute, recomputes preflight, and — when it is not ready —
 * records nothing at all: no attempt count, no last error, no audit row. The reason
 * exists only for as long as that tick. So this reads the preflight the screen already
 * holds rather than inventing a second source of truth.
 *
 * Returns `null` when preflight has not loaded or nothing is failing; the caller
 * distinguishes "waiting on X" from "everything passed, it goes on the next sweep".
 */
export function armedWaitingReason(
  checks: readonly PreflightCheckLike[] | null | undefined,
): string | null {
  if (!checks) return null;
  const failing = checks
    .filter((check) => check.status === 'fail')
    .map((check) => (check.message ?? '').trim())
    .filter(Boolean);
  if (failing.length === 0) return null;
  // One reason plus a count. Listing every failure turns a status pill into a report,
  // and the Review section below already itemizes them.
  return failing.length === 1
    ? `Waiting on: ${failing[0]}`
    : `Waiting on: ${failing[0]} (+${failing.length - 1} more)`;
}

/** Pin summaries carry the same projection as the campaign, including history. */
export function campaignPinKind(pin: { campaign_status: CampaignStatus; operational_status?: CampaignOperationalStatus | null }): CampaignStatusKind {
  return campaignStatusKind(pin.campaign_status, null, pin.operational_status);
}

/**
 * Nothing more will run: every flight is over (New York's day, on the server), or
 * the campaign was archived. The detail page then says what ran, not what is
 * live (2026-10-19). Whether delivery really stopped is the state's business:
 * an archived campaign reads `pausing` until its stop is seen (2026-10-21), so
 * archived is the stored status, not the state.
 */
export function campaignIsOver(operational: CampaignOperationalStatus | null | undefined): boolean {
  return operational?.phase === 'finished' || operational?.requested_state === 'ended' || operational?.state === 'ended';
}

/** An automatic-pause action always preserves paused delivery, even when the
 * editor opened before another tab changed the requested state. */
export function campaignPauseAction(current: CampaignOperationalStatus | null | undefined, captured: string): 'live' | 'paused' {
  if (current?.state === 'paused_by_system') return 'paused';
  return (current?.requested_state ?? captured) === 'live' ? 'paused' : 'live';
}
